import { useState, useRef } from 'react';
import { useAuthStore } from '../../lib/auth';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Download, Upload, RefreshCw, AlertTriangle, FileJson, Clock, Loader2, Trash2, CheckSquare, Square
} from 'lucide-react';

export default function ManualBackup() {
  const fileInputRef = useRef(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [confirmRestoreDialog, setConfirmRestoreDialog] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastBackup, setLastBackup] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [backupFormat, setBackupFormat] = useState('gzip');

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => new Intl.NumberFormat('tr-TR').format(num || 0);

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      setProgress(10);
      const { accessToken } = useAuthStore.getState();
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      if (!accessToken) throw new Error('Oturum suresi dolmus.');

      const response = await fetch(`${baseUrl}/api/admin/database/backup?format=${backupFormat}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      setProgress(50);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Backup failed');
      }
      const blob = await response.blob();
      setProgress(90);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = backupFormat === 'gzip' ? '.json.gz' : '.json';
      let filename = `mongodb_backup_${new Date().toISOString().split('T')[0]}_${Date.now()}${ext}`;
      const cd = response.headers.get('Content-Disposition');
      if (cd) { const m = cd.match(/filename="?([^"]+)"?/); if (m) filename = m[1]; }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setLastBackup(new Date().toISOString());
      setProgress(100);
      toast.success('Veritabani yedegi olusturuldu ve indirildi');
    } catch (error) {
      toast.error(error.message || 'Yedekleme hatasi');
    } finally {
      setBackupLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) { toast.error('Lutfen .json formatinda bir yedek dosyasi secin'); return; }
    try {
      const text = await file.text();
      let data = JSON.parse(text);
      if (data.success && data.data) data = data.data;
      if (!data.backup_info || !data.collections) { toast.error('Gecersiz yedek dosyasi formati.'); return; }
      setRestoreFile(file);
      setRestorePreview(data);
      setSelectedCollections(new Set(Object.keys(data.collections)));
      toast.success('Yedek dosyasi yuklendi.');
    } catch (error) {
      toast.error('Dosya okunamadi veya gecersiz JSON formati');
    }
    event.target.value = '';
  };

  const toggleCollection = (name) => {
    setSelectedCollections(prev => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : s.add(name);
      return s;
    });
  };

  const toggleAllCollections = () => {
    if (!restorePreview?.collections) return;
    const all = Object.keys(restorePreview.collections);
    setSelectedCollections(prev => prev.size === all.length ? new Set() : new Set(all));
  };

  const handleRestore = async () => {
    if (!restorePreview || selectedCollections.size === 0) { toast.error('Lutfen en az bir tablo secin'); return; }
    try {
      setRestoreLoading(true);
      setProgress(10);
      const filtered = {};
      for (const [name, data] of Object.entries(restorePreview.collections)) {
        if (selectedCollections.has(name)) filtered[name] = data;
      }
      const response = await api.post('/admin/database/restore', {
        backup_data: { backup_info: restorePreview.backup_info, collections: filtered }
      });
      setProgress(100);
      if (response.data.success) {
        toast.success(`${selectedCollections.size} tablo basariyla geri yuklendi`);
        setRestoreFile(null);
        setRestorePreview(null);
        setSelectedCollections(new Set());
        setConfirmRestoreDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri yukleme hatasi');
    } finally {
      setRestoreLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  return (
    <>
      {progress > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex-1"><Progress value={progress} className="h-2" /></div>
              <span className="text-sm text-slate-500">{progress}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />Yedek Olustur
            </CardTitle>
            <CardDescription>Tum veritabani koleksiyonlarinin tam yedegini olusturun</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
              <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-3">Yedeklenecek Koleksiyonlar:</h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Veritabanindaki <strong>tum koleksiyonlar</strong> otomatik olarak yedeklenir (users, firms, invoices, invoice_employees, routes, vehicles, vb.)
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Format:</span>
              <Select value={backupFormat} onValueChange={setBackupFormat}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gzip">.json.gz (Sikistirilmis)</SelectItem>
                  <SelectItem value="json">.json (Normal)</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-400">
                {backupFormat === 'gzip' ? '~70% daha kucuk dosya' : 'Okunabilir format'}
              </span>
            </div>
            {lastBackup && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />Son yedek: {new Date(lastBackup).toLocaleString('tr-TR')}
              </div>
            )}
            <Button onClick={handleCreateBackup} disabled={backupLoading} className="w-full" size="lg" data-testid="create-backup-btn">
              {backupLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Yedek Olusturuluyor...</>) : (<><Download className="h-4 w-4 mr-2" />Tam Yedek Olustur ve Indir</>)}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-600" />Yedekten Geri Yukle
            </CardTitle>
            <CardDescription>Daha once alinmis bir yedegi geri yukleyin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileSelect} />
            {!restorePreview ? (
              <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-orange-400 transition-colors" onClick={() => fileInputRef.current?.click()} data-testid="restore-upload-area">
                <FileJson className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 dark:text-slate-300 mb-2">Yedek dosyasini secmek icin tiklayin</p>
                <p className="text-xs text-slate-400">Desteklenen format: .json</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-orange-800 dark:text-orange-200">Yedek Dosyasi Yuklendi</h4>
                      <p className="text-sm text-orange-600 dark:text-orange-300 mt-1">Geri yukleme mevcut verilerin uzerine yazacaktir. Dikkatli olun!</p>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between"><span className="text-slate-500">Yedek Tarihi:</span><span className="font-medium">{restorePreview.backup_info?.created_at ? new Date(restorePreview.backup_info.created_at).toLocaleString('tr-TR') : 'Bilinmiyor'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Versiyon:</span><Badge variant="outline">{restorePreview.backup_info?.version || '1.0'}</Badge></div>
                  <div className="flex justify-between"><span className="text-slate-500">Dosya:</span><span className="font-medium text-sm">{restoreFile?.name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Boyut:</span><span className="font-medium">{formatBytes(restoreFile?.size)}</span></div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-700 dark:text-slate-200">Geri Yuklenecek Tablolar ({selectedCollections.size}/{Object.keys(restorePreview.collections || {}).length})</h4>
                    <Button variant="ghost" size="sm" onClick={toggleAllCollections} className="text-xs h-7">
                      {selectedCollections.size === Object.keys(restorePreview.collections || {}).length ? (<><Square className="h-3 w-3 mr-1" />Tumunu Kaldir</>) : (<><CheckSquare className="h-3 w-3 mr-1" />Tumunu Sec</>)}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm max-h-64 overflow-y-auto">
                    {Object.entries(restorePreview.collections || {}).map(([name, data]) => (
                      <div key={name} className={`flex items-center gap-2 rounded px-3 py-2 cursor-pointer transition-colors ${selectedCollections.has(name) ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700' : 'bg-white dark:bg-slate-700 border border-transparent hover:border-slate-200'}`} onClick={() => toggleCollection(name)}>
                        <Checkbox checked={selectedCollections.has(name)} onCheckedChange={() => toggleCollection(name)} className="pointer-events-none" />
                        <span className={`flex-1 ${selectedCollections.has(name) ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-600 dark:text-slate-300'}`}>{name}</span>
                        <Badge variant={selectedCollections.has(name) ? "default" : "secondary"} className="text-xs">{formatNumber(Array.isArray(data) ? data.length : 0)}</Badge>
                      </div>
                    ))}
                  </div>
                  {selectedCollections.size === 0 && <p className="text-xs text-red-500 mt-2">En az bir tablo secmelisiniz</p>}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setRestoreFile(null); setRestorePreview(null); }} className="flex-1"><Trash2 className="h-4 w-4 mr-2" />Iptal</Button>
                  <Button variant="destructive" onClick={() => setConfirmRestoreDialog(true)} disabled={restoreLoading || selectedCollections.size === 0} className="flex-1" data-testid="restore-confirm-btn">
                    {restoreLoading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Geri Yukleniyor...</>) : (<><RefreshCw className="h-4 w-4 mr-2" />{selectedCollections.size > 0 ? `${selectedCollections.size} Tablo Geri Yukle` : 'Tablo Secin'}</>)}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Warning */}
      <Card className="border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">Onemli Uyarilar</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-700 dark:text-amber-300 list-disc list-inside">
                <li>Yedekleme islemi sunucu kaynaklarini kullanir</li>
                <li>Geri yukleme mevcut tum verilerin uzerine yazar - bu islem geri alinamaz</li>
                <li>Yedek dosyalarini guvenli bir yerde saklayin</li>
                <li>Buyuk veritabanlari icin yedekleme/geri yukleme birkac dakika surebilir</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <AlertDialog open={confirmRestoreDialog} onOpenChange={setConfirmRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />Geri Yuklemeyi Onayla</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-2">Bu islem secilen <strong>{selectedCollections.size}</strong> tablodaki mevcut verileri silecek ve yedekteki verilerle degistirecektir.</p>
                <div className="bg-slate-100 dark:bg-slate-800 rounded p-2 text-xs max-h-32 overflow-y-auto mb-2">
                  {Array.from(selectedCollections).map(name => (<span key={name} className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded mr-1 mb-1">{name}</span>))}
                </div>
                <strong className="text-red-600">Bu islem geri alinamaz!</strong>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Iptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} className="bg-red-600 hover:bg-red-700">Evet, {selectedCollections.size} Tablo Geri Yukle</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
